import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Stethoscope } from "lucide-react";
import { useBearEntTree } from "@/hooks/useBearEntTree";

/**
 * 2-column mega-menu panel for the ENT catalog.
 *
 * Usage: render inside a dropdown/popover container — the component itself
 * provides the list of categories + the right-side instrument-type panel.
 *
 * Clicking a category slug → /sterilemate/ent-<slug>
 * Clicking a specific instrument type → /sterilemate/ent-<slug>?type=<type>
 */
export default function EntMegaMenuPanel({ onNavigate }) {
  const { tree, loading } = useBearEntTree();
  const [hovered, setHovered] = useState(null);

  const selected = tree.find((c) => c.slug === hovered) || tree[0];

  if (loading && tree.length === 0) {
    return <div className="p-4 text-sm text-slate-400">Loading ENT catalog…</div>;
  }

  const catLink = (c) => `/sterilemate/ent-${c.slug.replace(/^ent-/, "")}`.replace("ent-ent-", "ent-");

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] w-full" data-testid="ent-mega-menu">
      {/* Left column: categories */}
      <div className="border-r border-slate-800 py-2">
        {tree.map((c) => (
          <Link
            key={c.slug}
            to={catLink(c)}
            onMouseEnter={() => setHovered(c.slug)}
            onFocus={() => setHovered(c.slug)}
            onClick={onNavigate}
            className={`flex items-center justify-between gap-2 px-4 py-2 text-sm transition-colors ${
              selected?.slug === c.slug
                ? "bg-purple-500/15 text-purple-200"
                : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
            }`}
            data-testid={`ent-mega-cat-${c.slug}`}>
            <div className="min-w-0">
              <div className="font-medium truncate">{c.name.replace(/\s*\([^)]*\)\s*$/, "")}</div>
              <div className="text-[10px] text-slate-500">{c.count} products</div>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          </Link>
        ))}
      </div>

      {/* Right column: instrument types inside the hovered category */}
      <div className="py-2 max-h-[60vh] overflow-y-auto">
        {selected && (
          <>
            <Link
              to={catLink(selected)}
              onClick={onNavigate}
              className="block px-4 py-2 text-xs font-semibold text-purple-300 uppercase tracking-wider hover:text-purple-200"
              data-testid={`ent-mega-view-all-${selected.slug}`}>
              <Stethoscope className="w-3 h-3 inline mr-1" />
              View All {selected.name.split("(")[0].trim()} ({selected.count})
            </Link>
            {selected.types.length === 0 ? (
              <p className="px-4 py-2 text-xs text-slate-500">No sub-types indexed.</p>
            ) : (
              selected.types.map((t) => (
                <Link
                  key={t.name}
                  to={`${catLink(selected)}?type=${encodeURIComponent(t.name)}`}
                  onClick={onNavigate}
                  className="flex items-center justify-between gap-2 px-4 py-1.5 text-sm text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  data-testid={`ent-mega-type-${selected.slug}-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <span className="truncate">{t.name}</span>
                  <span className="text-[10px] text-slate-500 shrink-0">({t.count})</span>
                </Link>
              ))
            )}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/ent-catalog`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
              className="flex items-center gap-2 mx-2 mt-3 px-4 py-2.5 text-xs font-semibold bg-amber-500/15 border border-amber-400/30 text-amber-200 hover:bg-amber-500/25 hover:text-amber-100 rounded-lg transition-colors"
              data-testid="ent-mega-download-catalog">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 shrink-0">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <span className="truncate">Download full ENT Catalog PDF</span>
            </a>
          </>
        )}
      </div>
    </div>
  );
}
